import { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Trophy, ArrowRight, ChevronLeft,
    Shield, BarChart3, Zap,
    CheckCircle2, Loader2, X, Star, Users,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';


/**
 * MandatoOnetWizard — Modal riutilizzabile a 4 step per attivare
 * il Mandato Diretto ONET Utilities (usato da Sidebar, Comparatore, Fatturazione).
 */
const MandatoOnetWizard = ({ isOpen, onClose, onSuccess }) => {
    const { profile, effectiveProfile, refreshProfile } = useAuth();
    const ep = effectiveProfile || profile;

    const [step,       setStep]       = useState(1);
    const [accepted,   setAccepted]   = useState(false);
    const [submitting, setSubmitting] = useState(false);

    if (!isOpen) return null;

    const handleClose = () => {
        if (submitting) return;
        setStep(1);
        setAccepted(false);
        onClose?.();
    };

    const handleActivate = async () => {
        if (!accepted) { toast.error('Devi accettare i termini per continuare'); return; }
        setSubmitting(true);
        try {
            // 0. Trova il manager ONET designato (Armando Iannone) per collegare l'utente alla rete
            let onetManagerId = null;
            try {
                const { data: managers } = await supabase
                    .from('users')
                    .select('id')
                    .eq('email', 'armando.iannone@apophis.it')
                    .in('role', ['manager', 'admin'])
                    .limit(1);
                onetManagerId = managers?.[0]?.id || null;
            } catch (e) {
                console.warn('[mandate] impossibile trovare manager ONET:', e);
            }

            // 1. Aggiorna user — imposta mandato + manager_id ONET
            const { error: userErr } = await supabase
                .from('users')
                .update({
                    has_mandato_onet: true,
                    ...(onetManagerId ? { manager_id: onetManagerId } : {}),
                })
                .eq('id', ep.id);
            if (userErr) throw new Error('Errore aggiornamento profilo: ' + userErr.message);

            // 2. Aggiorna organizzazione
            if (ep.organization_id) {
                await supabase.from('organizations').update({
                    subscription_plan:   'onet_mandate',
                    subscription_tier:   'free',
                    subscription_price:  0,
                    subscription_status: 'active',
                }).eq('id', ep.organization_id);
            }

            // 3. Notifica ONET (non bloccante)
            supabase.functions
                .invoke('send-mandate-notification', { body: {} })
                .catch(e => console.warn('[mandate] notifica non inviata:', e));

            // 4. Successo
            setStep(4);
            toast.success('Mandato ONET attivato! Benvenuto nel Championship.');

            setTimeout(() => {
                refreshProfile?.(ep.id).catch(() => {});
                onSuccess?.();
            }, 2500);

        } catch (err) {
            toast.error(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Card */}
            <motion.div
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                onClick={e => e.stopPropagation()}
                className="relative w-full max-w-sm rounded-2xl overflow-hidden z-10"
                style={{
                    background:     'rgba(8,3,18,0.95)',
                    border:         '1px solid rgba(124,58,237,0.4)',
                    boxShadow:      '0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(139,92,246,0.1)',
                    backdropFilter: 'blur(20px)',
                }}
            >
                {/* Close button */}
                {step < 4 && (
                    <button
                        onClick={handleClose}
                        className="absolute top-3 right-3 p-1.5 rounded-lg text-gray-600 hover:text-gray-400 hover:bg-white/5 transition z-10"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}

                {/* Step dots */}
                <div className="flex items-center justify-center gap-2 pt-5 pb-1">
                    {[1, 2, 3].map(s => (
                        <motion.div
                            key={s}
                            animate={{
                                width:      step === s ? 20 : 6,
                                background: step >= s
                                    ? 'linear-gradient(90deg,#8b5cf6,#ec4899)'
                                    : 'rgba(255,255,255,0.12)',
                            }}
                            transition={{ duration: 0.25 }}
                            className="h-1.5 rounded-full"
                        />
                    ))}
                </div>

                <AnimatePresence mode="wait">

                    {/* ── Step 1: Ottieni il mandato diretto ONET ── */}
                    {step === 1 && (
                        <motion.div key="ms1"
                            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.22 }}
                            className="px-6 pt-4 pb-6 space-y-4"
                        >
                            {/* Header */}
                            <div className="text-center space-y-2">
                                <div
                                    className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto"
                                    style={{ background: 'linear-gradient(135deg,#059669,#10b981)', boxShadow: '0 8px 24px rgba(16,185,129,0.3)' }}
                                >
                                    <Star className="w-7 h-7 text-white" fill="white" />
                                </div>
                                <div>
                                    <p className="text-white font-black text-lg leading-tight">Ottieni il Mandato<br />Diretto ONET</p>
                                    <p className="text-gray-400 text-xs mt-1.5 leading-relaxed">
                                        Accedi gratuitamente a tutti gli strumenti CRM ONET firmando il mandato diretto come agente energia.
                                    </p>
                                </div>
                            </div>

                            {/* Benefits list */}
                            <div className="space-y-2">
                                {[
                                    { icon: Shield,   color: '#10b981', label: 'CRM completamente gratuito',      sub: 'Nessun canone mensile, nessuna sorpresa' },
                                    { icon: Zap,      color: '#f59e0b', label: 'Comparatore AI a crediti',        sub: '10 crediti gratuiti al primo accesso' },
                                    { icon: BarChart3, color: '#8b5cf6', label: '20+ fornitori energy',            sub: 'Luce, gas, internet — censimento completo' },
                                    { icon: Trophy,   color: '#f97316', label: 'ONET Championship',               sub: 'Classifica annuale + premi di produzione' },
                                    { icon: Users,    color: '#3b82f6', label: 'Gestione clienti & contratti',    sub: 'CRM completo incluso nel piano' },
                                ].map(({ icon: Icon, color, label, sub }) => (
                                    <div key={label}
                                        className="flex items-center gap-3 rounded-xl px-3 py-2.5"
                                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
                                    >
                                        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                                            style={{ background: `${color}22` }}>
                                            <Icon className="w-3.5 h-3.5" style={{ color }} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-white text-xs font-semibold">{label}</p>
                                            <p className="text-gray-500 text-[10px] leading-tight mt-0.5">{sub}</p>
                                        </div>
                                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                                    </div>
                                ))}
                            </div>

                            <button
                                onClick={() => setStep(2)}
                                className="w-full py-2.5 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
                                style={{ background: 'linear-gradient(135deg,#059669,#10b981)' }}
                            >
                                Continua <ArrowRight className="w-4 h-4" />
                            </button>
                        </motion.div>
                    )}

                    {/* ── Step 2: Vantaggi ── */}
                    {step === 2 && (
                        <motion.div key="ms2"
                            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.22 }}
                            className="px-6 pt-4 pb-6 space-y-4"
                        >
                            <p className="text-white font-black text-base text-center">Cosa ottieni con il mandato</p>
                            <div className="grid grid-cols-2 gap-2">
                                {[
                                    { icon: <Shield   className="w-4 h-4" />, title: 'Account gratuito', desc: 'Nessun abbonamento mensile',   color: '#10b981' },
                                    { icon: <BarChart3 className="w-4 h-4" />, title: '20+ fornitori',    desc: 'Censimento energy completo',   color: '#8b5cf6' },
                                    { icon: <Zap      className="w-4 h-4" />, title: 'Crediti comparatore', desc: 'Confronta offerte con AI', color: '#f59e0b' },
                                    { icon: <Trophy   className="w-4 h-4" />, title: 'Championship',     desc: 'Premi di produzione annuali',   color: '#f97316' },
                                ].map(({ icon, title, desc, color }) => (
                                    <div key={title} className="rounded-xl p-3 space-y-1"
                                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                                        <span style={{ color }}>{icon}</span>
                                        <p className="text-white text-xs font-bold">{title}</p>
                                        <p className="text-gray-500 text-[10px] leading-tight">{desc}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => setStep(1)}
                                    className="px-4 py-2.5 rounded-xl text-xs font-semibold text-violet-300/60 hover:text-violet-300 transition-colors flex items-center gap-1"
                                    style={{ border: '1px solid rgba(139,92,246,0.2)' }}>
                                    <ChevronLeft className="w-3.5 h-3.5" /> Indietro
                                </button>
                                <button
                                    onClick={() => setStep(3)}
                                    className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
                                    style={{ background: 'linear-gradient(135deg,#059669,#10b981)' }}
                                >
                                    Come richiederlo <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* ── Step 3: Conferma ── */}
                    {step === 3 && (
                        <motion.div key="ms3"
                            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.22 }}
                            className="px-6 pt-4 pb-6 space-y-4"
                        >
                            <p className="text-white font-black text-base text-center">Attiva il tuo mandato</p>

                            <div className="rounded-xl p-3.5 space-y-2"
                                style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.25)' }}>
                                <p className="text-[10px] font-bold text-emerald-400/70 uppercase tracking-widest">Cosa cambia nel tuo account</p>
                                {[
                                    ['Piano',        'Mandato ONET Utilities (gratuito)'],
                                    ['Comparatore',  'A crediti (10 crediti di benvenuto)'],
                                    ['Championship', 'Accesso completo + premi produzione'],
                                    ['Censimento',   '20+ fornitori energy'],
                                ].map(([k, v]) => (
                                    <div key={k} className="flex justify-between text-xs">
                                        <span className="text-gray-500">{k}</span>
                                        <span className="text-white font-semibold">{v}</span>
                                    </div>
                                ))}
                            </div>

                            <button
                                onClick={() => setAccepted(v => !v)}
                                className="w-full flex items-start gap-3 text-left p-3 rounded-xl transition-colors"
                                style={{
                                    background: accepted ? 'rgba(16,185,129,0.08)' : 'rgba(255,255,255,0.03)',
                                    border:     accepted ? '1px solid rgba(16,185,129,0.4)' : '1px solid rgba(255,255,255,0.08)',
                                }}
                            >
                                <div className="w-4 h-4 rounded flex-shrink-0 mt-0.5 flex items-center justify-center"
                                    style={{ background: accepted ? '#10b981' : 'rgba(255,255,255,0.1)', border: accepted ? 'none' : '1px solid rgba(255,255,255,0.2)' }}>
                                    {accepted && <CheckCircle2 className="w-3 h-3 text-white" />}
                                </div>
                                <p className="text-xs text-gray-400 leading-relaxed">
                                    Confermo di operare come agente con{' '}
                                    <span className="text-white font-semibold">Mandato Diretto ONET Utilities</span>
                                    {' '}per luce e gas e accetto di passare al piano gratuito a crediti.
                                </p>
                            </button>

                            <div className="flex gap-2">
                                <button onClick={() => setStep(2)}
                                    disabled={submitting}
                                    className="px-4 py-2.5 rounded-xl text-xs font-semibold text-violet-300/60 hover:text-violet-300 transition-colors flex items-center gap-1 disabled:opacity-40"
                                    style={{ border: '1px solid rgba(139,92,246,0.2)' }}>
                                    <ChevronLeft className="w-3.5 h-3.5" /> Indietro
                                </button>
                                <motion.button
                                    whileHover={!submitting && accepted ? { scale: 1.02 } : {}}
                                    whileTap={!submitting && accepted ? { scale: 0.98 } : {}}
                                    onClick={handleActivate}
                                    disabled={submitting || !accepted}
                                    className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 disabled:opacity-40"
                                    style={{ background: 'linear-gradient(135deg,#059669,#10b981)' }}
                                >
                                    {submitting
                                        ? <><motion.div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                                            animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }} />
                                            Attivazione...</>
                                        : <>Attiva il Mandato ONET <ArrowRight className="w-4 h-4" /></>}
                                </motion.button>
                            </div>
                        </motion.div>
                    )}

                    {/* ── Step 4: Successo ── */}
                    {step === 4 && (
                        <motion.div key="ms4"
                            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                            className="px-6 pt-6 pb-8 text-center space-y-4"
                        >
                            <motion.div
                                initial={{ scale: 0 }} animate={{ scale: 1 }}
                                transition={{ delay: 0.15, type: 'spring', stiffness: 260, damping: 20 }}
                                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto"
                                style={{ background: 'linear-gradient(135deg,#059669,#10b981)', boxShadow: '0 0 32px rgba(16,185,129,0.5)' }}
                            >
                                <CheckCircle2 className="w-8 h-8 text-white" />
                            </motion.div>
                            <div>
                                <p className="text-white font-black text-xl mb-2">Mandato attivato! 🎉</p>
                                <p className="text-gray-400 text-sm leading-relaxed">
                                    Il tuo account è ora gratuito a crediti ONET.<br />
                                    La Championship si sta sbloccando...
                                </p>
                            </div>
                            <motion.div
                                animate={{ opacity: [0.4, 1, 0.4] }}
                                transition={{ duration: 1.5, repeat: Infinity }}
                                className="flex items-center justify-center gap-2 text-xs text-emerald-400"
                            >
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                Caricamento in corso...
                            </motion.div>
                        </motion.div>
                    )}

                </AnimatePresence>
            </motion.div>
        </div>
    , document.body);
};

export default MandatoOnetWizard;
